/**
 * Returns a deep copy of the value with every string trimmed (leading/trailing whitespace removed).
 * Recurses into plain objects and arrays; leaves other types (numbers, booleans, null, Date, class
 * instances, etc.) untouched. Used to normalize form payloads before submitting them to the server so
 * stray whitespace a user types/pastes into a field never reaches the backend.
 */
export function deepTrim<T>(value: T): T {
    if (typeof value === 'string') {
        return value.trim() as unknown as T;
    }
    if (Array.isArray(value)) {
        return value.map((v) => deepTrim(v)) as unknown as T;
    }
    if (value !== null && typeof value === 'object') {
        // Only recurse into plain objects; leave class instances (e.g. Date) as-is.
        const proto = Object.getPrototypeOf(value);
        if (proto === Object.prototype || proto === null) {
            const result: Record<string, unknown> = {};
            for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
                result[key] = deepTrim(v);
            }
            return result as unknown as T;
        }
    }
    return value;
}
